// NextTaskModal — the "next task" composer for a playbook step.
//
// This popup lets the user assemble the work for the CURRENT playbook
// step out of mode-specific blocks (Chat / Image / Research / Analyze /
// Prompt Builder) without leaving the unified chat shell. Each block
// carries its own prompt + per-block model picker, so a single step
// can chain "research the topic with Sonar Pro → draft copy with
// GPT-5 → generate a hero image with Nano Banana Pro" — and every
// block lands in the same shared transcript so memory carries
// forward across LLM switches.
//
// Why a modal (not inline cards): playbook steps can need 2–5 sub-tasks
// and surfacing them all inline would crowd the composer. A modal also
// gives us room to label each block clearly with its model and intent
// before the user fires it. We auto-close after submission so the
// user immediately sees their results streaming into the transcript.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Trash2,
  Send,
  MessageSquare,
  ImageIcon,
  Globe,
  FileText,
  Wand2,
  GripVertical,
  Loader2,
  RotateCcw,
  Paperclip,
  X as XIcon,
  Braces,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
// Drag-and-drop primitives — same stack the mastery-hero settings use,
// so we get accessible keyboard reordering and pointer DnD without
// inventing our own. Each block becomes a sortable row keyed on its
// stable client id.
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

// --- Block types ------------------------------------------------------

export type NextTaskBlockKind =
  | "chat"
  | "image"
  | "research"
  | "analyze"
  | "builder";

export interface NextTaskBlock {
  /** Stable client-side id so React lists & deletion behave. */
  id: string;
  kind: NextTaskBlockKind;
  /** Model id (chat/image/research). For analyze, this carries the task id. */
  model: string;
  prompt: string;
  /** Optional file attachment for image/research/analyze blocks.
   *  - image: used as the source/edit reference (data URL).
   *  - analyze: text files are inlined into the prompt; binary
   *    files (PDFs/images) are referenced by name so the runner can
   *    surface them in the transcript.
   *  - research: filename is appended to the query as context. */
  attachment?: NextTaskAttachment;
}

export interface NextTaskAttachment {
  /** Original filename, shown in the chip and forwarded to the runner. */
  name: string;
  /** MIME type from the File object (e.g. "image/png", "application/pdf"). */
  mime: string;
  /** Size in bytes — used to enforce the 10 MB cap and show a hint. */
  size: number;
  /** Base64 data URL for binary files (images/PDFs). Omitted for text. */
  dataUrl?: string;
  /** Decoded UTF-8 contents for text-like files (.txt, .md, .csv, .json…). */
  text?: string;
}

// --- Variables -------------------------------------------------------
// Prompts inside any block can reference `{topic}`, `{source}`, etc.
// At runtime we substitute every `{name}` token using two layers:
//   1. The shell-level `variables` map passed into the modal (currently
//      `{ topic, step, stepTitle }` — the things every block in this
//      step shares).
//   2. Per-block overrides that the runner injects right before
//      executing — most importantly `{source}`, which points at the
//      block's own attached file (text contents or filename) so the
//      same template can be reused across blocks that each pick a
//      different document.
//
// Anything still unresolved after both passes is left as-is so users
// see exactly which token they mistyped instead of getting a silent
// empty string.

/** Replace every `{name}` token in `text` with the matching value from
 *  `vars`. Names are matched case-insensitively and stripped of
 *  surrounding whitespace so `{ Topic }` and `{topic}` both resolve.
 *  Unknown tokens are preserved verbatim. */
export function applyVariables(
  text: string,
  vars: Record<string, string | undefined>,
): string {
  if (!text) return text;
  // Build a lower-cased lookup once so the replace callback stays O(1).
  const lookup: Record<string, string> = {};
  for (const [k, v] of Object.entries(vars)) {
    if (v == null) continue;
    lookup[k.trim().toLowerCase()] = v;
  }
  return text.replace(/\{([a-zA-Z0-9_]+)\}/g, (full, name: string) => {
    const hit = lookup[name.trim().toLowerCase()];
    return hit !== undefined ? hit : full;
  });
}

/** Compute the per-block `{source}` value (and any other block-scoped
 *  variables) so callers don't have to duplicate this rule. We prefer
 *  the inlined text contents (truncated for safety) so the template
 *  reads naturally; otherwise fall back to the filename so the prompt
 *  still mentions the document by name. */
export function blockScopedVariables(
  block: NextTaskBlock,
): Record<string, string | undefined> {
  const att = block.attachment;
  if (!att) return { source: undefined };
  if (att.text && att.text.trim()) {
    // 6 KB cap mirrors the research/analyze inlining cap in the
    // shell — keeps a single `{source}` substitution from blowing
    // past the model's context window in the common case.
    const trimmed = att.text.length > 6000 ? `${att.text.slice(0, 6000)}\n…[truncated]` : att.text;
    return { source: trimmed };
  }
  return { source: att.name };
}

// --- Catalog ---------------------------------------------------------
// Mirror of the model lists from UnifiedChatShell. We duplicate them
// here so the modal can stay self-contained — keeping these lightweight
// arrays in two places is cheaper than threading a registry through
// props and the shell already accepts the matching ids.

const CHAT_MODELS = [
  { id: "google/gemini-3-flash-preview", label: "Gemini 3 Flash" },
  { id: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { id: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { id: "google/gemini-2.5-flash-lite", label: "Gemini Flash Lite" },
  { id: "openai/gpt-5", label: "GPT-5" },
  { id: "openai/gpt-5-mini", label: "GPT-5 Mini" },
  { id: "openai/gpt-5-nano", label: "GPT-5 Nano" },
];
const IMAGE_MODELS = [
  { id: "google/gemini-2.5-flash-image", label: "Nano Banana (fast)" },
  { id: "google/gemini-3.1-flash-image-preview", label: "Nano Banana 2 (pro, fast)" },
  { id: "google/gemini-3-pro-image-preview", label: "Nano Banana Pro (best)" },
];
const RESEARCH_MODELS = [
  { id: "sonar", label: "Sonar (fast)" },
  { id: "sonar-pro", label: "Sonar Pro" },
  { id: "sonar-reasoning", label: "Sonar Reasoning" },
];
const ANALYZE_TASKS = [
  { id: "summarize", label: "Summarize" },
  { id: "explain", label: "Explain simply" },
  { id: "refactor", label: "Refactor code" },
  { id: "extract_action_items", label: "Extract action items" },
];

const KIND_META: Record<
  NextTaskBlockKind,
  { label: string; icon: typeof MessageSquare; hint: string; defaultModel: string; options: { id: string; label: string }[] }
> = {
  chat: {
    label: "Chat",
    icon: MessageSquare,
    hint: "Talk it through with any model.",
    defaultModel: CHAT_MODELS[0].id,
    options: CHAT_MODELS,
  },
  image: {
    label: "Image",
    icon: ImageIcon,
    hint: "Generate an image to go with the step.",
    defaultModel: IMAGE_MODELS[0].id,
    options: IMAGE_MODELS,
  },
  research: {
    label: "Research",
    icon: Globe,
    hint: "Web-grounded answer with citations.",
    defaultModel: RESEARCH_MODELS[0].id,
    options: RESEARCH_MODELS,
  },
  analyze: {
    label: "Analyze",
    icon: FileText,
    hint: "Summarize, explain, or extract from text.",
    defaultModel: ANALYZE_TASKS[0].id,
    options: ANALYZE_TASKS,
  },
  builder: {
    label: "Prompt builder",
    icon: Wand2,
    hint: "Rewrite a rough idea as a structured prompt.",
    defaultModel: CHAT_MODELS[0].id,
    options: CHAT_MODELS,
  },
};

const ALL_KINDS: NextTaskBlockKind[] = [
  "chat",
  "image",
  "research",
  "analyze",
  "builder",
];

/** Block kinds that accept a file attachment. Chat/builder don't —
 *  the underlying chat endpoint doesn't accept files yet, and adding
 *  an unused picker would be confusing. */
const ATTACHABLE_KINDS: ReadonlySet<NextTaskBlockKind> = new Set([
  "image",
  "research",
  "analyze",
]);

/** 10 MB cap. Keeps localStorage drafts manageable (data URLs balloon
 *  ~33%) and matches the rough upper bound of what the analyze/image
 *  endpoints can chew through in a single call. */
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Accept hints per block kind — narrows the OS file picker so
 *  the user doesn't pick something the runner can't use. */
const ACCEPT_FOR: Record<NextTaskBlockKind, string> = {
  chat: "",
  builder: "",
  image: "image/*",
  research: ".pdf,.txt,.md,.csv,.json,application/pdf,text/*",
  analyze: ".pdf,.txt,.md,.csv,.json,.tsv,.log,application/pdf,text/*",
};

/** Heuristic: treat anything starting with `text/` plus a small list
 *  of common code/data extensions as text we can decode and inline. */
function isTextLike(file: File): boolean {
  if (file.type.startsWith("text/")) return true;
  if (
    file.type === "application/json" ||
    file.type === "application/xml" ||
    file.type === "application/javascript"
  )
    return true;
  return /\.(txt|md|markdown|csv|tsv|json|xml|log|yaml|yml|html|js|ts|tsx|jsx|css|py|sql)$/i.test(
    file.name,
  );
}

/** Read the file as the right shape: text for text-likes (so we can
 *  inline contents), data URL for binary so it round-trips through the
 *  runner / shows up as an image source. */
function readAttachment(file: File): Promise<NextTaskAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    if (isTextLike(file)) {
      reader.onload = () =>
        resolve({
          name: file.name,
          mime: file.type || "text/plain",
          size: file.size,
          text: typeof reader.result === "string" ? reader.result : "",
        });
      reader.readAsText(file);
    } else {
      reader.onload = () =>
        resolve({
          name: file.name,
          mime: file.type || "application/octet-stream",
          size: file.size,
          dataUrl: typeof reader.result === "string" ? reader.result : "",
        });
      reader.readAsDataURL(file);
    }
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Props -----------------------------------------------------------

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Step number (1-indexed) the popup is composing for. */
  stepNumber: number;
  /** Optional human title for the step, shown in the dialog header. */
  stepTitle?: string;
  /** Initial prompt to seed the first chat block (usually the step's
   *  default prompt with topic substituted). */
  initialPrompt?: string;
  /** Stable identifier for the parent playbook. Combined with
   *  stepNumber to scope the localStorage draft so different playbooks
   *  (and different steps within one) keep independent block stacks.
   *  When omitted, draft persistence is disabled — useful for ad-hoc
   *  callers that don't want sticky state. */
  playbookSlug?: string;
  /** Step-level variables available to every block's prompt as
   *  `{name}` tokens (e.g. `{ topic: "cold email", stepTitle: "Draft" }`).
   *  The runner gets the same map back via `onSubmit` so it can perform
   *  the actual substitution alongside per-block extras like `{source}`.
   *  Both keys and tokens are matched case-insensitively. */
  variables?: Record<string, string | undefined>;
  /** Caller runs each block sequentially against the shared transcript. */
  onSubmit: (blocks: NextTaskBlock[]) => Promise<void> | void;
}

let nextId = 0;
const newId = () => `b${Date.now().toString(36)}-${++nextId}`;

function makeBlock(kind: NextTaskBlockKind, prompt = ""): NextTaskBlock {
  return {
    id: newId(),
    kind,
    model: KIND_META[kind].defaultModel,
    prompt,
  };
}

// --- Draft persistence ----------------------------------------------
// Block stacks are saved to localStorage so closing the modal (or
// refreshing the page) doesn't lose work. The key is scoped by
// playbook slug + step so each step keeps its own draft.
const DRAFT_KEY = (slug: string, step: number) =>
  `hl:nexttask:draft:${slug}:${step}`;

function loadDraft(slug: string | undefined, step: number): NextTaskBlock[] | null {
  if (!slug || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY(slug, step));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Light validation — drop anything missing required fields so a
    // corrupt/legacy entry can't crash the modal.
    const valid = parsed.filter(
      (b) =>
        b &&
        typeof b.id === "string" &&
        typeof b.prompt === "string" &&
        typeof b.model === "string" &&
        ALL_KINDS.includes(b.kind),
    ) as NextTaskBlock[];
    return valid.length > 0 ? valid : null;
  } catch {
    return null;
  }
}

function saveDraft(
  slug: string | undefined,
  step: number,
  blocks: NextTaskBlock[],
) {
  if (!slug || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DRAFT_KEY(slug, step), JSON.stringify(blocks));
  } catch {
    // Quota / private mode — silent failure is fine; the user just
    // loses persistence for this session.
  }
}

function clearDraft(slug: string | undefined, step: number) {
  if (!slug || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_KEY(slug, step));
  } catch {
    // ignore
  }
}

export default function NextTaskModal({
  open,
  onOpenChange,
  stepNumber,
  stepTitle,
  initialPrompt = "",
  playbookSlug,
  variables,
  onSubmit,
}: Props) {
  // The block list is the user's plan for this step. On first mount
  // (and whenever a different step opens) we prefer a saved draft, so
  // a user who closed the modal mid-thought picks up exactly where
  // they left off. Otherwise we seed with a single chat block carrying
  // the step's default prompt so the most common flow is one tap.
  const [blocks, setBlocks] = useState<NextTaskBlock[]>(
    () =>
      loadDraft(playbookSlug, stepNumber) ?? [makeBlock("chat", initialPrompt)],
  );
  // Tracks whether the current block list is a restored draft, so we
  // can show a small "draft restored" affordance with a one-tap reset.
  const [restoredDraft, setRestoredDraft] = useState<boolean>(
    () => !!loadDraft(playbookSlug, stepNumber),
  );
  const [submitting, setSubmitting] = useState(false);

  // When the popup opens (for any step), prefer the saved draft for
  // that exact step over the seeded default. This is what makes the
  // composer feel sticky across closes/refreshes.
  useEffect(() => {
    if (open) {
      const draft = loadDraft(playbookSlug, stepNumber);
      if (draft) {
        setBlocks(draft);
        setRestoredDraft(true);
      } else {
        setBlocks([makeBlock("chat", initialPrompt)]);
        setRestoredDraft(false);
      }
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepNumber, playbookSlug]);

  // Persist on every change while the modal is open. Cheap (a single
  // setItem) and keeps the draft fresh even if the tab is closed
  // unexpectedly.
  useEffect(() => {
    if (!open) return;
    saveDraft(playbookSlug, stepNumber, blocks);
  }, [open, playbookSlug, stepNumber, blocks]);

  const addBlock = (kind: NextTaskBlockKind) => {
    setBlocks((prev) => [...prev, makeBlock(kind)]);
  };

  const removeBlock = (id: string) => {
    setBlocks((prev) => (prev.length > 1 ? prev.filter((b) => b.id !== id) : prev));
  };

  const updateBlock = (id: string, patch: Partial<NextTaskBlock>) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  /** Discard the saved draft and reseed with the default chat block.
   *  Useful when the restored draft is no longer relevant (different
   *  topic, stale prompt, etc.). */
  const resetDraft = () => {
    clearDraft(playbookSlug, stepNumber);
    setBlocks([makeBlock("chat", initialPrompt)]);
    setRestoredDraft(false);
  };

  // DnD sensors — small activation distance so a click on the grip
  // doesn't get treated as a drag, and keyboard support so the order
  // is operable without a mouse.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks((prev) => {
      const oldIndex = prev.findIndex((b) => b.id === active.id);
      const newIndex = prev.findIndex((b) => b.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  // Block is ready when it has a non-empty prompt — analyze + research
  // both need text to operate on, chat/image obviously do too.
  const ready = useMemo(
    () => blocks.length > 0 && blocks.every((b) => b.prompt.trim().length > 0),
    [blocks],
  );

  // The set of `{name}` tokens we can offer the user as click-to-insert
  // chips. Always advertises `{source}` even when no shell-level vars
  // exist, because every block can attach a file. Sorted so the order
  // is stable across renders.
  const availableVarNames = useMemo(() => {
    const names = new Set<string>(["source"]);
    if (variables) {
      for (const [k, v] of Object.entries(variables)) {
        if (v != null && k.trim()) names.add(k.trim().toLowerCase());
      }
    }
    return Array.from(names).sort();
  }, [variables]);

  const handleRun = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(blocks);
      // The stack just executed — drop the draft so reopening the step
      // starts fresh instead of replaying the same plan.
      clearDraft(playbookSlug, stepNumber);
      setRestoredDraft(false);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-mono text-base flex items-center gap-2">
            <span className="px-1.5 py-0.5 rounded bg-primary/15 text-primary text-[10px] font-mono">
              Step {stepNumber}
            </span>
            <span className="truncate">{stepTitle ?? "Next task"}</span>
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] text-muted-foreground">
            Stack chat, image, research, analyze, and prompt-builder
            blocks for this step. They run in order against the same
            transcript — switching models never drops your memory.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {restoredDraft && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5">
              <span className="text-[10px] font-mono text-primary">
                Draft restored from your last visit.
              </span>
              <button
                type="button"
                onClick={resetDraft}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors"
                title="Discard the saved draft and start over"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            </div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={blocks.map((b) => b.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-3 list-none p-0 m-0">
                {blocks.map((b, i) => (
                  <SortableBlockRow
                    key={b.id}
                    block={b}
                    index={i}
                    canRemove={blocks.length > 1}
                    onRemove={() => removeBlock(b.id)}
                    onUpdate={(patch) => updateBlock(b.id, patch)}
                    availableVarNames={availableVarNames}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <div className="pt-1">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
              Add a block
            </p>
            <div className="flex flex-wrap gap-1.5">
              {ALL_KINDS.map((k) => {
                const m = KIND_META[k];
                const Icon = m.icon;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => addBlock(k)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-border text-[11px] font-mono text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                    title={m.hint}
                  >
                    <Plus className="w-3 h-3" />
                    <Icon className="w-3 h-3" />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-border px-5 py-3 flex items-center justify-between gap-3 bg-background/40">
          <span className="text-[10px] font-mono text-muted-foreground">
            {blocks.length} block{blocks.length === 1 ? "" : "s"} · drag to reorder · variables auto-fill at run time
          </span>
          <button
            type="button"
            onClick={handleRun}
            disabled={!ready || submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground text-xs font-mono font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Run step
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Sortable row ---------------------------------------------------
// Rendered for each block. The drag handle is the GripVertical button;
// the rest of the row stays interactive (model picker, textarea, trash)
// because @dnd-kit only listens on the handle's spread props.
function SortableBlockRow({
  block,
  index,
  canRemove,
  onRemove,
  onUpdate,
  availableVarNames,
}: {
  block: NextTaskBlock;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  onUpdate: (patch: Partial<NextTaskBlock>) => void;
  /** Lower-cased variable names this block can reference as `{name}`.
   *  Rendered as click-to-insert chips below the textarea so users
   *  don't have to memorise which tokens are available. */
  availableVarNames: string[];
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const meta = KIND_META[block.kind];
  const Icon = meta.icon;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const supportsAttachment = ATTACHABLE_KINDS.has(block.kind);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (file.size > ATTACHMENT_MAX_BYTES) {
      toast.error(`Attachment must be under ${formatBytes(ATTACHMENT_MAX_BYTES)}`);
      return;
    }
    try {
      const att = await readAttachment(file);
      onUpdate({ attachment: att });
    } catch {
      toast.error("Could not read that file. Try another.");
    }
  };

  const clearAttachment = () => onUpdate({ attachment: undefined });

  /** Insert `{name}` at the caret (or replace the current selection)
   *  inside the textarea. Falls back to appending when the textarea
   *  isn't focused yet. Keeping this client-side avoids a controlled-
   *  input round-trip that would jump the caret to the end. */
  const insertVariable = (name: string) => {
    const token = `{${name}}`;
    const ta = textareaRef.current;
    if (!ta) {
      onUpdate({ prompt: `${block.prompt}${block.prompt && !block.prompt.endsWith(" ") ? " " : ""}${token}` });
      return;
    }
    const start = ta.selectionStart ?? block.prompt.length;
    const end = ta.selectionEnd ?? block.prompt.length;
    const next = `${block.prompt.slice(0, start)}${token}${block.prompt.slice(end)}`;
    onUpdate({ prompt: next });
    // Restore caret right after the inserted token on the next frame
    // (state update re-renders the value first).
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border bg-card/60 p-3 space-y-2 transition-colors ${
        isDragging ? "border-primary shadow-lg" : "border-border"
      }`}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="shrink-0 cursor-grab active:cursor-grabbing touch-none p-1 -m-1 rounded text-muted-foreground/60 hover:text-foreground"
          aria-label={`Reorder ${meta.label} block`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <span className="text-[10px] font-mono text-muted-foreground tabular-nums w-5 shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-mono">
          <Icon className="w-3 h-3" />
          {meta.label}
        </span>
        <select
          value={block.model}
          onChange={(e) => onUpdate({ model: e.target.value })}
          className="bg-secondary border border-border rounded px-1.5 py-0.5 text-[10px] font-mono text-foreground focus:outline-none focus:border-primary"
          aria-label={`${meta.label} model`}
        >
          {meta.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground hidden sm:inline">
          {meta.hint}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${meta.label} block`}
            className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <textarea
        ref={textareaRef}
        value={block.prompt}
        onChange={(e) => onUpdate({ prompt: e.target.value })}
        rows={3}
        placeholder={
          block.kind === "image"
            ? "Describe the image…"
            : block.kind === "research"
              ? "What should we research?"
              : block.kind === "analyze"
                ? "Paste the text or code to analyze…"
                : block.kind === "builder"
                  ? "Rough idea — we'll rewrite it as a structured prompt…"
                  : "What should the model do?"
        }
        className="w-full resize-none bg-secondary border border-border rounded-md px-2 py-1.5 text-[12px] font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
      />
      {availableVarNames.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
            <Braces className="w-3 h-3" />
            Insert
          </span>
          {availableVarNames.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => insertVariable(name)}
              className="inline-flex items-center px-1.5 py-0.5 rounded border border-border bg-secondary/60 text-[10px] font-mono text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
              title={
                name === "source"
                  ? "Inserts the contents (or filename) of this block's attached file at run time."
                  : `Inserts the value of {${name}} at run time.`
              }
            >
              {`{${name}}`}
            </button>
          ))}
        </div>
      )}
      {supportsAttachment && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_FOR[block.kind]}
            onChange={handleFileChange}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
          />
          {block.attachment ? (
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-primary/30 bg-primary/5 text-[10px] font-mono text-primary max-w-full"
              title={`${block.attachment.name} · ${formatBytes(block.attachment.size)}`}
            >
              <Paperclip className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[200px]">{block.attachment.name}</span>
              <span className="text-muted-foreground tabular-nums">
                {formatBytes(block.attachment.size)}
              </span>
              <button
                type="button"
                onClick={clearAttachment}
                aria-label={`Remove attachment ${block.attachment.name}`}
                className="ml-1 rounded text-muted-foreground hover:text-destructive transition-colors"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={handlePickFile}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed border-border text-[10px] font-mono text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              title={
                block.kind === "image"
                  ? "Attach an image to edit or use as reference"
                  : block.kind === "analyze"
                    ? "Attach a PDF or text/code file to analyze"
                    : "Attach a PDF or text file as research context"
              }
            >
              <Paperclip className="w-3 h-3" />
              Attach file
            </button>
          )}
        </div>
      )}
    </li>
  );
}